/**
 * Complex AG Grid cell renderers that require React components.
 * For simple value formatters, use formatters.js instead.
 */

import React from 'react';
import { Box, Typography, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

// ============================================
// Product List Cell Renderer
// ============================================

/**
 * Renders a row's products as product tags (linked to product pages in dev).
 * @param {Object} options - Configuration options
 * @param {React.Component} options.ProductTag - ProductTag component
 * @param {string} options.field - Row field holding [{ id, name }] offerings
 * @param {string} options.empty - Text when the row has no products
 * @returns {Function} AG Grid cell renderer function
 */
export const createProductListCellRenderer = ({ ProductTag, field = 'offerings', empty = '—' }) => (params) => {
  const offerings = params.data?.[field] || [];
  if (!offerings.length) return empty;
  return (
    <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', columnGap: 0.75, rowGap: 0.25 }}>
      {offerings.map((offering, index) => (
        <Box component="span" key={offering.id || offering.name}>
          <ProductTag offeringId={offering.id} name={offering.name} showIcon={false} />
          {index < offerings.length - 1 ? ',' : ''}
        </Box>
      ))}
    </Box>
  );
};

// ============================================
// Company Name Cell Renderer
// ============================================

/**
 * Creates a company name cell renderer with ticker icon and link
 * @param {Object} options - Configuration options
 * @param {Object} options.theme - MUI theme object
 * @param {string} options.alignment - 'compsheet' or 'top15'
 * @param {React.Component} options.TickerIcon - TickerIcon component
 * @param {Function} options.companyPathBuilder - Optional function that returns the company link path
 * @param {Function} options.onCompanyClick - Optional click handler for company links
 * @returns {Function} AG Grid cell renderer function
 */
export const createCompanyCellRenderer = ({
  theme,
  alignment,
  TickerIcon,
  companyPathBuilder = (ticker) => `/company/${ticker?.toLowerCase()}/keymetrics`,
  onCompanyClick,
}) => (params) => {
  const ticker = params.data?.['Company Symbol'];
  const companyName = params.value;
  const row = params.node.rowIndex + 1;
  const isTotal = companyName === 'Median' || companyName === 'Mean';
  const companyPath = !isTotal && ticker ? companyPathBuilder(ticker, params.data) : undefined;
  const handleCompanyClick = () => onCompanyClick?.(ticker, params.data);

  if (alignment === 'compsheet') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {!isTotal && (
          <TickerIcon
            ticker={ticker}
            sourcePriority="sprite-first"
            size={18}
            style={{ opacity: 0.8, marginRight: 6, flexShrink: 0 }}
          />
        )}
        {!isTotal && (
          <Link component={RouterLink} to={companyPath} onClick={handleCompanyClick}>
            {companyName}
          </Link>
        )}
        {isTotal && companyName}
      </Box>
    );
  }

  // Top 15 view with ranking
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
        {!isTotal && (
          <Typography
            sx={{
              fontSize: '13px',
              color: theme.palette.text.secondary,
              fontWeight: 'bold',
              marginLeft: '-4px',
              marginRight: '2px',
            }}
          >
            {`#${row}: `}
          </Typography>
        )}
        {!isTotal && (
          <Link component={RouterLink} to={companyPath} onClick={handleCompanyClick}>
            {companyName}
          </Link>
        )}
        {isTotal && companyName}
      </Box>
      {!isTotal && (
        <TickerIcon ticker={ticker} sourcePriority="sprite-first" size={18} style={{ opacity: 0.8, flexShrink: 0 }} />
      )}
    </Box>
  );
};

// ============================================
// Colored Percentage Cell Renderer
// ============================================

/**
 * Creates a percentage cell renderer with positive/negative coloring
 * @param {Object} theme - MUI theme object
 * @returns {Function} AG Grid cell renderer function
 */
export const createColoredPercentageCellRenderer = (theme) => (params) => {
  const { value } = params;

  if (value === null || value === undefined || isNaN(value)) {
    return <span>-</span>;
  }

  const percentage = Math.abs(value) < 1
    ? (value * 100).toFixed(1)
    : (value * 100).toFixed(0);

  const color = value >= 0 ? theme.palette.text.green : theme.palette.text.red;
  const prefix = value >= 0 ? '+' : '';

  return (
    <span style={{ color }}>
      {`${prefix}${percentage}%`}
    </span>
  );
};

// ============================================
// Link Cell Renderer
// ============================================

/**
 * Creates a cell renderer that wraps value in a RouterLink
 * @param {Function} linkBuilder - Function that takes row data and returns link path
 * @returns {Function} AG Grid cell renderer function
 */
export const createLinkCellRenderer = (linkBuilder) => (params) => {
  const linkPath = linkBuilder(params.data);

  if (!linkPath) {
    return params.value;
  }

  return (
    <Link component={RouterLink} to={linkPath}>
      {params.value}
    </Link>
  );
};

// ============================================
// Checkbox Cell Renderer Factory
// ============================================

/**
 * Creates a checkbox cell renderer for boolean columns
 * @param {Object} options - Configuration options
 * @param {Array} options.checkedItems - Array of checked item values
 * @param {Function} options.onToggle - Callback when checkbox toggled
 * @param {Object} options.theme - MUI theme object
 * @returns {Function} AG Grid cell renderer function
 */
export const createCheckboxCellRenderer = ({ checkedItems, onToggle, theme }) => (params) => {
  const { value, data } = params;
  const isChecked = checkedItems.includes(value);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
      }}
      onClick={() => onToggle(value)}
    >
      <Box
        sx={{
          width: 16,
          height: 16,
          border: `2px solid ${theme.palette.primary.main}`,
          borderRadius: '3px',
          backgroundColor: isChecked ? theme.palette.primary.main : 'transparent',
          marginRight: 1,
        }}
      />
      {data?.label || value}
    </Box>
  );
};

// ============================================
// Icon Cell Renderer
// ============================================

/**
 * Creates a cell renderer that displays an icon based on value
 * @param {Object} iconMap - Map of values to icon components
 * @param {React.Component} DefaultIcon - Default icon if value not in map
 * @returns {Function} AG Grid cell renderer function
 */
export const createIconCellRenderer = (iconMap, DefaultIcon = null) => (params) => {
  const Icon = iconMap[params.value] || DefaultIcon;

  if (!Icon) {
    return params.value;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Icon fontSize="small" />
      {params.value}
    </Box>
  );
};

// ============================================
// Field with Sub-Field Renderer
// ============================================

/**
 * Creates a cell renderer with a main field and smaller sub-field below
 * Useful for showing metric name with description
 * @param {Object} theme - MUI theme object
 * @returns {Function} AG Grid cell renderer function
 */
export const createFieldWithSubFieldRenderer = (theme) => (params) => {
  const mainValue = params.value;
  const subValue = params.data?.subField || params.data?.description;

  if (!subValue) {
    return mainValue;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '12px' }}>
        {mainValue}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: theme.palette.text.secondary, fontSize: '10px' }}
      >
        {subValue}
      </Typography>
    </Box>
  );
};

// ============================================
// IB Financial Cell Renderer
// ============================================

/**
 * Creates a cell renderer for IB-style financial data
 * Uses parentheses for negatives, handles special formatting
 * @param {Object} theme - MUI theme object
 * @param {string} format - 'currency', 'percentage', 'multiple', 'number'
 * @returns {Function} AG Grid cell renderer function
 */
export const createIBFinancialCellRenderer = (theme, format = 'currency') => {
  const formatValue = (value) => {
    if (value === null || value === undefined || value === '' || isNaN(value)) {
      return '-';
    }

    const num = parseFloat(value);
    const absValue = Math.abs(num);
    const isNegative = num < 0;

    let formatted;
    switch (format) {
      case 'percentage':
        formatted = `${absValue.toFixed(1)}%`;
        break;
      case 'multiple':
        formatted = `${absValue.toFixed(1)}x`;
        break;
      case 'currency':
        if (absValue >= 1_000_000_000) {
          formatted = `$${(absValue / 1_000_000_000).toFixed(1)}B`;
        } else if (absValue >= 1_000_000) {
          formatted = `$${(absValue / 1_000_000).toFixed(1)}M`;
        } else if (absValue >= 1_000) {
          formatted = `$${(absValue / 1_000).toFixed(0)}K`;
        } else {
          formatted = `$${absValue.toFixed(0)}`;
        }
        break;
      case 'number':
      default:
        formatted = absValue.toLocaleString();
    }

    return isNegative ? `(${formatted})` : formatted;
  };

  return (params) => {
    const value = params.value;
    const formatted = formatValue(value);

    // Check if negative for styling
    const isNegative = parseFloat(value) < 0;

    return (
      <span style={{ color: isNegative ? theme.palette.text.red : undefined }}>
        {formatted}
      </span>
    );
  };
};

// ============================================
// Ticker Cell Renderer (with optional icon)
// ============================================

/**
 * Creates a ticker symbol cell renderer with optional icon
 * @param {Object} options - Configuration options
 * @param {Object} options.theme - MUI theme object
 * @param {React.Component} options.TickerIcon - TickerIcon component
 * @param {boolean} options.showIcon - Whether to show the icon
 * @param {boolean} options.linkToCompany - Whether to link to company page
 * @returns {Function} AG Grid cell renderer function
 */
export const createTickerCellRenderer = ({
  theme,
  TickerIcon,
  showIcon = true,
  linkToCompany = true,
}) => (params) => {
  const ticker = params.value;

  if (!ticker) {
    return '-';
  }

  const content = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {showIcon && TickerIcon && (
        <TickerIcon
          ticker={ticker}
          sourcePriority="sprite-first"
          size={16}
          style={{ opacity: 0.8, flexShrink: 0 }}
        />
      )}
      <span>{ticker}</span>
    </Box>
  );

  if (linkToCompany) {
    return (
      <Link component={RouterLink} to={`/company/${ticker.toLowerCase()}/keymetrics`}>
        {content}
      </Link>
    );
  }

  return content;
};

// ============================================
// Date Cell Renderer
// ============================================

/**
 * Creates a date cell renderer with configurable format
 * @param {string} format - 'short', 'long', 'relative'
 * @returns {Function} AG Grid cell renderer function
 */
export const createDateCellRenderer = (format = 'short') => (params) => {
  const value = params.value;

  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return value;
  }

  switch (format) {
    case 'short':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    case 'long':
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    case 'relative': {
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    default:
      return date.toLocaleDateString();
  }
};

// ============================================
// Badge Cell Renderer
// ============================================

/**
 * Creates a badge/chip cell renderer for status or category columns
 * @param {Object} options - Configuration options
 * @param {Object} options.theme - MUI theme object
 * @param {Object} options.colorMap - Map of values to colors
 * @returns {Function} AG Grid cell renderer function
 */
export const createBadgeCellRenderer = ({ theme, colorMap = {} }) => (params) => {
  const value = params.value;

  if (!value) {
    return '-';
  }

  const color = colorMap[value] || theme.palette.grey[500];

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: 1,
        backgroundColor: `${color}20`,
        border: `1px solid ${color}`,
        fontSize: '11px',
        fontWeight: 500,
        color: color,
      }}
    >
      {value}
    </Box>
  );
};
